// @ts-strict-ignore
import {
  Component, EventEmitter, Input,
  Output, ViewChild
} from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { LMContextService, LMLoggerService } from '@app/services';
import { LMModel } from '@form/model';
import {
  LMGenAIPromptGeneratedEvent,
  LMGenAIPromptMessageObject,
  LMGenAIPromptResponse
} from '@modal/lm-genai-prompt-dialog';
import {
  SohoAlertDirective,
  SohoButtonComponent,
  SohoEditorComponent,
} from 'ids-enterprise-ng';
import * as DOMPurify from 'dompurify';

@Component({
  selector:     'lm-genai-prompt',
  templateUrl:  'lm-genai-prompt-dialog.component.html',
  styleUrls:    [ 'lm-genai-prompt-dialog.component.scss' ],
})

export class LMGenAIPromptDialogComponent {
  constructor(private http: HttpClient,
    private lmContextService: LMContextService,
    private lmLoggerService: LMLoggerService,) {}

  @Input() generatedText: string;
  @Input() model: LMModel;
  @Input() fieldId: string;
  @Input() dataView: LMDataView;
  @Input() url: string;
  @Output() generatedTextEvent = new EventEmitter<LMGenAIPromptGeneratedEvent>();
  @ViewChild(SohoEditorComponent) generatedField: SohoEditorComponent;
  @ViewChild('generateButton') public generateButton: SohoButtonComponent;
  @ViewChild(SohoAlertDirective) alert: SohoAlertDirective;

  public valid = false;
  public isGeneratedError = false;
  public generatedErrorMessage = '';
  public disclaimerMessage = Soho.Locale.translate('GeneratedAIDisclaimerMessage');

  public genAIPrompt = {
    value: ''
  };

  public genAIResponse = {
    value: ''
  };

  public getGeneratedText() {
    return this.genAIResponse.value;
  }

  public getDataView() {
    return this.dataView;
  }

  onInput(event: Event) {
    if (this.genAIPrompt.value.length > 0) {
      this.valid = true;
    } else {
      this.valid = false;
    }
  }

  onGenerateClickEvent(event: Event) {
    const promptValue = DOMPurify.sanitize(this.genAIPrompt.value);

    if (promptValue.length <= 0) {
      return;
    }

    this.valid = false;
    this.genAIResponse.value = '';
    this.generatedField.writeValue(this.genAIResponse.value);

    this.generateAssistedText()
      .subscribe((response: LMGenAIPromptResponse) => {
        if (response.status === 'VALID') {
          (this.generatedField as any).editor.getCurrentElement().find('.ai-loading-indicator').remove();
          this.isGeneratedError = false;

          this.generatedErrorMessage = '';
          this.genAIResponse.value = DOMPurify.sanitize(response.text, {FORBID_TAGS: ['style', 'form', 'button', 'a', 'input', 'select', 'textarea']});
          this.generatedField.writeValue(this.genAIResponse.value);

          const lmGeneratedTextEvent: LMGenAIPromptGeneratedEvent = <LMGenAIPromptGeneratedEvent>{ valid: true };
          this.generatedTextEvent.emit(lmGeneratedTextEvent);
        } else if (response.error) {
          (this.generatedField as any).editor.getCurrentElement().find('.ai-loading-indicator').remove();
          this.isGeneratedError = true;

          if  (typeof response.error.message === 'object') {
            const error: LMGenAIPromptMessageObject = response.error.message;
            if (typeof error.message === 'string') {
              this.generatedErrorMessage = error.message;
            }
          } else if (typeof response.error.message === 'string') {
            this.generatedErrorMessage = response.error.message;
          } else {
            this.generatedErrorMessage = '';
          }

          this.genAIResponse.value = '';
          this.generatedField.writeValue(this.genAIResponse.value);

          const lmGeneratedTextEvent: LMGenAIPromptGeneratedEvent = <LMGenAIPromptGeneratedEvent>{ valid: false };
          this.generatedTextEvent.emit(lmGeneratedTextEvent);
        }
        this.valid = true;
      });
  }

  generateAssistedText(): Observable<LMGenAIPromptResponse> {
    const loadingIndicator = $('<div class="ai-loading-indicator"><div class="bar"></div><div class="bar"></div><div class="bar"></div></div>');

    const currentElement = (this.generatedField as any).editor.getCurrentElement();
    loadingIndicator.appendTo(currentElement);

    const formRequest = {
      requestType: 'genAIPrompt',
      prompt: this.genAIPrompt.value,
    };

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');

    return this.http.post<LMGenAIPromptResponse>(this.url, JSON.stringify(formRequest), { headers }).pipe(
      map((response: LMGenAIPromptResponse) => {
        return response;
      }),
      catchError(error => this._handleError(error)));
  }

  private _handleError(error: Response) {
    // in a real world app, we may send the error to some remote logging infrastructure
    // instead of just logging it to the console
    this.lmLoggerService.log(error);
    return throwError(error || 'Server error');
  }
}
